import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoArrowBack } from 'react-icons/io5';
import { useTenant } from '../Context/TenantContext';
import API_BASE_URL from '../Config/config';
import { hasTsgAiAccess, TSG_AI_URL } from '../utils/tsgAiAccess';
import { useSubscription } from '../hooks/useSubscription';
 
export default function SubscriptionPlans() {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const subscription = useSubscription();
  const { isPaid: isPaidSubscription, plan: subscriptionPlan } = subscription;
  const isPaidUser = isPaidSubscription || subscriptionPlan === 'college';
  const showDiscountChallenge = !isPaidUser;
  const [notice, setNotice] = useState("");

  const isMainPlatform = !tenant || 
                         tenant.domain === 'theskillgenie.com' || 
                         tenant.tenantKey === 'skillgenie' ||
                         window.location.hostname === 'theskillgenie.com' ||
                         window.location.hostname === 'www.theskillgenie.com' ||
                         window.location.hostname === 'localhost';

  useEffect(() => {
    if (tenant && !isMainPlatform) {
      setNotice(`Full access is provided by ${tenant.collegeName}. No subscription required.`);
    }
  }, [tenant, isMainPlatform]);
 const [selectedPlanId, setSelectedPlanId] = useState(null);
 const [summary, setSummary] = useState(null); // {label, amount, duration, endDate}
 const [hasUsedTrial, setHasUsedTrial] = useState(false);
 const [isStartingTrial, setIsStartingTrial] = useState(false);
 const [isTeamUser, setIsTeamUser] = useState(false);
 const [payments, setPayments] = useState([]);
 const [hasCheckedSubscription, setHasCheckedSubscription] = useState(false);
 const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(false);
 const [showPaymentHistory, setShowPaymentHistory] = useState(false);
 const [buyerCount, setBuyerCount] = useState(0);
 const [loadingCount, setLoadingCount] = useState(true);
 const [showAlreadySubscribedModal, setShowAlreadySubscribedModal] = useState(false);
 
 const userId = useMemo(() => {
 try {
 const user = JSON.parse(localStorage.getItem("user"));
 // Try user_code first (which is stored as user_id in mock_interview_payments), then fallback to userId
 return user?.user_code || user?.user_id || user?.userId || localStorage.getItem('user_id') ||"UnknownUser";
 } catch (e) { return"UnknownUser"; }
 }, []);

  // Check if user has already used trial, TSG AI access, and if user is on team roles
  useEffect(() => {
    const checkTrialUsageAndTsgAccess = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const response = await fetch(`${API_BASE_URL}/api/skillgenie-premium/status/${userId}`, {
          signal: controller.signal
        });
        clearTimeout(timeoutId);

        if (response.ok) {
          const data = await response.json();
          setHasUsedTrial(data.trialUsed || false);
        
          // Check for team membership in the backend status
          // Note: Backend might need to return isTeamMember for this to work perfectly, 
          // but we also have local check if needed.
          if (data.isTeamMember) {
            setIsTeamUser(true);
          }
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          console.warn('⚠️ Trial status API timeout - trial button will be available');
        } else {
          console.warn('⚠️ Trial status API unavailable (502/CORS) - trial button will be available');
        }
        // Don't block trial button if API is unavailable
        setHasUsedTrial(false);
      }
    };

    const checkTeamStatusLocally = () => {
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        // Some users might have role or is_team_member in their profile
        if (user?.role === 'team' || user?.is_team_member || user?.teamId) {
          setIsTeamUser(true);
        }
      } catch (e) {
        console.error("Error checking team status locally", e);
      }
    };

    if (userId && userId !== 'UnknownUser') {
      checkTrialUsageAndTsgAccess();
      checkTeamStatusLocally();
    }
  }, [userId]);

  const [specialization, setSpecialization] = useState(localStorage.getItem("elite_specialization"));

  const handleSelectSpecialization = (tech) => {
    localStorage.setItem("elite_specialization", tech);
    setSpecialization(tech);
    navigate(`/topicexplorer?tech=${tech}`);
  };

  // Fetch premium buyer count
  useEffect(() => {
    const fetchBuyerCount = async () => {
      try {
        setLoadingCount(true);
        const response = await fetch(`${API_BASE_URL}/api/skillgenie-premium/count/premium`);
        if (response.ok) {
          const data = await response.json();
          if (data.ok) {
            setBuyerCount(data.count);
          }
        }
      } catch (error) {
        console.error("Failed to fetch buyer count:", error);
      } finally {
        setLoadingCount(false);
      }
    };
    fetchBuyerCount();
  }, []);

 // Fetch payment history
 useEffect(() => {
 const fetchPaymentHistory = async () => {
 if (!userId || userId === 'UnknownUser') return;
 
 try {
 setPaymentHistoryLoading(true);
 const response = await fetch(`${API_BASE_URL}/api/skillgenie-premium/user/${userId}`);
 if (response.ok) {
 const data = await response.json();
 setPayments(data.payments || []);
 setHasCheckedSubscription(true);
 }
 } catch (error) {
 console.error('Failed to fetch payment history:', error);
 } finally {
 setPaymentHistoryLoading(false);
 }
 };
 
 fetchPaymentHistory();
 }, [userId]);
 
  const basePlans = [
    { id: 'quarterly', price: 3999, label: 'Standard (3 Months)', duration: '3 months' },
    { id: 'halfyearly', price: 14999, label: 'Premium (6 Months)', duration: '6 months' },
    { id: 'yearly', price: 18999, label: 'Elite (1 Year)', duration: '1 year' }
  ];

  const plans = basePlans.map((p) => {
    // Apply one-time interview discount if available
    const pendingDiscount = localStorage.getItem('pending_discount'); // percentage for legacy
    const discountAmountStr = localStorage.getItem('pending_discount_amount');
    
    let payableAmount = p.price;
    let discountDisplay = "";
    let scholarshipType = "None";
    let actualDiscountAmount = 0;

    if ((discountAmountStr || pendingDiscount) && (p.id === 'halfyearly' || p.id === 'yearly')) {
        // Updated fixed range logic as per user requirement:
        // 6 Months (₹14,999) scholarship: ₹5,999 - ₹7,999 
        // 1 Year (₹18,999) scholarship: ₹9,999 - ₹10,999
        
        // Use pseudo-random based on userId to keep it stable per user
        const seed = userId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
        
        if (p.id === 'halfyearly') {
            // Target price: 5999 - 7999
            // Formula: Price - Discount = Target
            // Discount = Price - Target
            // Max Discount = 14999 - 5999 = 9000
            // Min Discount = 14999 - 7999 = 7000
            actualDiscountAmount = 7000 + (seed % (9000 - 7000 + 1));
        } else if (p.id === 'yearly') {
            actualDiscountAmount = 9999 + (seed % (10999 - 9999 + 1));
        }

        payableAmount = Math.max(0, p.price - actualDiscountAmount);
        discountDisplay = `₹${actualDiscountAmount.toLocaleString('en-IN')} Scholarship Applied`;
        scholarshipType = actualDiscountAmount >= 9999 ? "Platinum" : "Gold";
    }

    // Add GST for 3 months plan
    if (p.id === 'quarterly') {
        payableAmount = Math.round(payableAmount * 1.18);
    }

    return {
      id: p.id,
      price: payableAmount,
      payableAmount: payableAmount,
      originalPrice: p.price,
      basePrice: p.price,
      label: p.label,
      planName: p.label,
      tagline: p.id === 'quarterly' ? 'Perfect for getting started' : 
               p.id === 'halfyearly' ? 'Most popular choice' : 'Best value for long-term learners',
      duration: p.duration,
      discountPercentage: (pendingDiscount && !discountAmountStr) ? parseInt(pendingDiscount) : 0,
      discountAmount: actualDiscountAmount,
      scholarshipType,
      discount: discountDisplay || (buyerCount < 100 ? 'Early Bird' : 'Standard Pricing'),
      savingsLabel: (discountAmountStr || pendingDiscount) ? 'SCHOLARSHIP' : 'BASE PRICE',
      hasGst: p.id === 'quarterly'
    };
  });
 
 const allFeatures = [
 'Premium Industry Topics',
 'Elite Technology Mastery',
 'Mock Interviews',
 'LLM Assistant',
 'TheSkillGenie (TSG) AI Mentor',
 'Certificate System',
 'Full Platform Features'
 ];
 
 const startCheckout = async (planId) => {
     if (tenant && !isMainPlatform) {
       alert(`Full access is already provided by ${tenant.collegeName}. You don't need a personal subscription.`);
       return;
     }

     const pendingDiscount = localStorage.getItem('pending_discount');

     // Check if user already has an active premium subscription
     const hasPremium = payments.some(p => 
       p.status === 'success' && 
       (p.plan_name.toLowerCase().includes('premium') || p.plan_name.toLowerCase().includes('elite') || p.plan_name.toLowerCase().includes('java') || p.plan_name.toLowerCase().includes('python') || p.plan_name.toLowerCase().includes('skillgenie_premium')) &&
       (!p.end_date || new Date(p.end_date) > new Date())
     );

     if (hasPremium) {
       // Show custom modal for already subscribed
       setShowAlreadySubscribedModal(true);
       return;
     }

     // Only check subscription status once, don't block if payments already fetched
     if (!hasCheckedSubscription && paymentHistoryLoading) {
         setNotice('⌛ Checking subscription status, please wait...');
         return;
     }

  const plan = plans.find(p => p.id === planId);
 setSelectedPlanId(planId);

 if (!plan) return;
 
 // Allow users to proceed with payment regardless of existing access
 // Users may want to renew, upgrade, or purchase additional subscriptions
 setNotice(`Selected: ${plan?.label || ''} plan ${plan.discountPercentage > 0 ? `(Scholarship ${plan.discountPercentage}% Applied)` : ''}`);
 try {
 // create order
 const requestBody = { 
   planId: plan.id,
   discountPercentage: plan.discountPercentage
 };

 console.log('--- CREATE ORDER REQUEST ---');
 console.log('URL:', `${API_BASE_URL}/api/skillgenie-premium/create-order`);
 console.log('Payload:', JSON.stringify(requestBody, null, 2));

 const res = await fetch(`${API_BASE_URL}/api/skillgenie-premium/create-order`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify(requestBody)
 });

 const data = await res.json();
 console.log('--- CREATE ORDER RESPONSE ---');
 console.log('Status:', res.status);
 console.log('Body:', JSON.stringify(data, null, 2));

 if (!res.ok) {
   const errorMessage = data.error || data.message || 'Order failed';
   setNotice(`❌ ${errorMessage}`);
   throw new Error(errorMessage);
 }

 const { id: order_id, amount, currency, endDate, duration } = data.order;
 const razorpayKey = data.keyId;
 
 if (!razorpayKey) {
 throw new Error('Razorpay key not provided by backend');
 }
 
 // Debug: Log which key is being used
 console.log('🔑 Razorpay Key from backend:', razorpayKey.substring(0, 10) + '...');

 setSummary({ label: plan.label, amount, duration, endDate });

 const options = {
 // Use key from backend order (ensures it matches the order owner)
 key: razorpayKey,
 amount,
 currency,
 name: 'The SkillGenie',
 description: plan.label,
 order_id,
 handler: async function (response) {
 try {
                    const verifyRes = await fetch(`${API_BASE_URL}/api/skillgenie-premium/verify-payment`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({
 razorpay_order_id: response.razorpay_order_id,
 razorpay_payment_id: response.razorpay_payment_id,
 razorpay_signature: response.razorpay_signature,
 user_id: userId,
 })
 });
 const verifyData = await verifyRes.json();
                     if (verifyData.ok) {
                         setNotice('✅ Payment successful. Premium access activated.');
                         localStorage.removeItem('pending_discount'); // Clear percentage coupon
                         localStorage.removeItem('pending_discount_amount'); // Clear amount coupon
                        
                         // Wait a second then reload to ensure premium status is reflected globally
                         setTimeout(() => {
                             window.location.reload();
                         }, 1500);
                     } else {
                         throw new Error(verifyData.error || 'Verification failed');
                     }
 
 // Refresh payment history if it's visible
 if (showPaymentHistory) {
 const refreshResponse = await fetch(`${API_BASE_URL}/api/skillgenie-premium/user/${userId}`);
 if (refreshResponse.ok) {
 const refreshData = await refreshResponse.json();
 setPayments(refreshData.payments || []);
 }
 } else {
 // Auto-show payment history after successful payment
 setShowPaymentHistory(true);
 const refreshResponse = await fetch(`${API_BASE_URL}/api/skillgenie-premium/user/${userId}`);
 if (refreshResponse.ok) {
 const refreshData = await refreshResponse.json();
 setPayments(refreshData.payments || []);
 }
 }
 } catch (err) {
 setNotice('❌ Verification failed: ' + err.message);
 }
 },
 prefill: { name: userId },
 };
 const rzp = new window.Razorpay(options);
 rzp.open();
 } catch (err) {
 setNotice('❌ ' + err.message);
 }
 };

 const startTrial = async () => {
    if (isTeamUser) {
      setNotice('❌ Free trial is not available for bootcamp participants (team roles). Please use your bootcamp credentials for access.');
      return;
    }

    if (hasUsedTrial) {
      setNotice('❌ You have already used your 7-day trial. Please choose a subscription plan.');
      return;
    }

    if (userId === 'UnknownUser') {
      setNotice('❌ Please login to start your trial.');
      navigate('/login');
      return;
    }

    try {
      setIsStartingTrial(true);
 setNotice('🔄 Activating your 7-day free trial...');
 
 const res = await fetch(`${API_BASE_URL}/api/skillgenie-premium/start-trial`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ user_id: userId })
 });
 const data = await res.json();
 if (!data.ok) throw new Error(data.error || 'Failed to start trial');
 
 // Store trial data in localStorage with proper structure
 const startDate = data.payment?.start_date ? new Date(data.payment.start_date) : new Date();
 const endDate = data.payment?.end_date ? new Date(data.payment.end_date) : new Date(startDate.getTime() + 7 * 24 * 60 * 60 * 1000);
 
 const trialData = {
 startDate: startDate.toISOString(),
 endDate: endDate.toISOString(),
 daysLeft: 7
 };
 localStorage.setItem(`user_trial_started_${userId}`, JSON.stringify(trialData));
 setHasUsedTrial(true);
 
 setNotice('✅ 7-day LLM free trial activated! You now have access to LLM features for 7 days. Redirecting to dashboard...');
 
 // Immediately redirect to dashboard - no payment screen should appear
 navigate('/dashboard');
 } catch (err) {
 setNotice('❌ ' + err.message);
 setIsStartingTrial(false);
 }
 };
 
  return (
<div className="min-h-screen bg-bgMain text-body font-sans relative overflow-hidden">
{/* Background decoration */}
<div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -mr-48 -mt-48"></div>

<main className="max-w-6xl mx-auto px-6 py-10 relative z-10">
<button
onClick={() => navigate('/dashboard')}
className="bg-bgSection text-primary px-6 py-2 rounded-full font-bold border border-primary/30 hover:bg-primary hover:text-white transition-all shadow-lg mb-8 flex items-center gap-2"
>
← Back to Dashboard
</button>

<div className="text-center mb-16">
  {buyerCount < 100 ? (
    <div className="inline-block bg-gradient-to-r from-green-600 to-blue-600 text-white px-8 py-3 rounded-full text-sm font-bold mb-6 animate-bounce shadow-xl">
      🚀 HURRY! {100 - buyerCount} spots left at current price! 🚀
    </div>
  ) : (
    <div className="inline-block bg-gradient-to-r from-red-600 to-orange-600 text-white px-8 py-3 rounded-full text-sm font-bold mb-6 animate-pulse shadow-xl">
      ⚠️ Price increased due to high demand! ⚠️
    </div>
  )}

  {showDiscountChallenge && (
    <div className="mb-12 p-8 rounded-[40px] bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 border-4 border-white/10 shadow-3xl text-white relative overflow-hidden group">
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-20"></div>
      <div className="relative z-10">
        <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest mb-6 border border-white/20">
          <span className="animate-pulse">🔥</span> Limited Time Challenge <span className="animate-pulse">🔥</span>
        </div>
        <h2 className="text-4xl md:text-6xl font-black mb-4 tracking-tighter uppercase italic">Earn Up To 50% OFF!</h2>
        <p className="text-xl md:text-2xl text-purple-100 mb-8 font-medium max-w-3xl mx-auto leading-relaxed">
          Complete our <span className="text-yellow-400 font-black">One-Time AI Mock Interview Challenge</span> and get a massive discount on any premium plan based on your score!
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10 max-w-4xl mx-auto">
          <div className="bg-white/10 backdrop-blur-lg p-6 rounded-3xl border border-white/10 transform transition hover:scale-105">
            <div className="text-sm font-bold uppercase tracking-widest text-white/70 mb-2">6 Months (₹14,999)</div>
            <div className="text-2xl font-black text-yellow-400">Save ₹5,999 - ₹7,999</div>
          </div>
          <div className="bg-white/10 backdrop-blur-lg p-6 rounded-3xl border border-white/10 transform transition hover:scale-105">
            <div className="text-sm font-bold uppercase tracking-widest text-white/70 mb-2">1 Year (₹18,999)</div>
            <div className="text-2xl font-black text-blue-300">Save ₹9,999 - ₹10,999</div>
          </div>
        </div>
        <div className="mb-10 max-w-lg mx-auto bg-white/5 backdrop-blur-lg p-4 rounded-2xl border border-white/10">
          <div className="text-sm font-bold uppercase tracking-widest text-white/70 mb-1">3 Months</div>
          <div className="text-xl font-black text-gray-300">₹3,999 + 18% GST (No Discount)</div>
        </div>

        <button 
          onClick={() => navigate('/discount-interview')}
          className="px-12 py-5 bg-white text-indigo-900 rounded-2xl font-black text-xl shadow-2xl hover:scale-110 active:scale-95 transition-all uppercase tracking-tighter flex items-center gap-3 mx-auto"
        >
          Take The Challenge Now
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </button>
        <p className="mt-4 text-xs font-bold text-white/50 uppercase tracking-widest">Available only once per user. Coupons are valid for 24 hours.</p>
      </div>
      
      {/* Decorative elements */}
      <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-white/10 rounded-full blur-3xl group-hover:bg-white/20 transition-all duration-700"></div>
      <div className="absolute -top-20 -left-20 w-64 h-64 bg-indigo-500/20 rounded-full blur-3xl group-hover:bg-indigo-500/30 transition-all duration-700"></div>
    </div>
  )}

  <p className="text-muted max-w-2xl mx-auto text-lg md:text-xl font-medium leading-relaxed">
    Or select your subscription plan below. Get access to all premium features including mock interviews, TSG AI, and specialized technology mastery tracks.
  </p>
</div>

 {notice && (
 <div className={`mb-8 rounded-2xl border ${notice.startsWith('✅') ? 'border-green-500/30 bg-green-500/10 text-green-400' : 'border-primary/30 bg-primary/10 text-primary'} px-6 py-4 text-sm font-bold shadow-2xl`}>
 {notice}
 </div>
 )}

 {summary && (
 <div className="mb-8 rounded-[32px] border border-secondary/30 bg-[#1a1a1a] p-8 shadow-2xl relative overflow-hidden">
 <div className="absolute top-0 right-0 w-32 h-32 bg-secondary/10 rounded-full blur-3xl"></div>
 <div className="text-primary text-xs font-bold uppercase tracking-widest mb-2">Selected Plan</div>
 <div className="text-3xl font-extrabold text-white mb-2">{summary.label}</div>
 <div className="flex flex-col md:flex-row gap-6 text-gray-400 font-bold text-sm">
 <span className="whitespace-nowrap">Duration: <span className="text-white">{summary.duration}</span></span>
 <span className="whitespace-nowrap">Ends on: <span className="text-white">{new Date(summary.endDate).toLocaleDateString()}</span></span>
 </div>
 </div>
)}

 {isPaidUser && !specialization && (
   <div className="mb-16 p-10 rounded-[40px] border-4 border-dashed border-primary/30 bg-primary/5 text-center shadow-2xl backdrop-blur-sm animate-fadeIn">
     <h3 className="text-3xl font-black text-gray-900 mb-4 uppercase tracking-tighter">Choose Your Path to Excellence</h3>
     <p className="text-muted mb-8 font-medium">As a Premium Member, you get access to one Elite Technology Mastery track. Please select your specialization:</p>
     <div className="flex flex-col sm:flex-row justify-center gap-6">
       <button 
         onClick={() => handleSelectSpecialization('java')}
         className="px-10 py-4 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-2xl font-black shadow-xl hover:scale-105 transition-all uppercase tracking-widest"
       >
         Select Java Elite
       </button>
       <button 
         onClick={() => handleSelectSpecialization('python')}
         className="px-10 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl font-black shadow-xl hover:scale-105 transition-all uppercase tracking-widest"
       >
         Select Python Elite
       </button>
     </div>
   </div>
 )}

 {isPaidUser && specialization && (
   <div className="mb-16 p-8 rounded-[32px] border border-green-500/30 bg-green-500/10 text-center shadow-2xl">
     <div className="flex items-center justify-center gap-3 mb-2">
       <span className="text-2xl">✅</span>
       <h3 className="text-xl font-bold text-white uppercase tracking-tight">Access Unlocked: {specialization === 'java' ? 'Java' : 'Python'} Track</h3>
     </div>
     <p className="text-gray-300 text-sm mb-6 font-medium">You have successfully selected your specialization. You can now access all premium content for this technology.</p>
     <div className="flex justify-center gap-4">
       <button 
         onClick={() => navigate('/subscription-plans')} 
         className="px-6 py-3 bg-white/10 text-white rounded-xl font-bold hover:bg-white/20 transition-all uppercase text-xs tracking-widest border border-white/20 flex items-center gap-2"
       >
         <IoArrowBack /> Back
       </button>
       <button 
         onClick={() => navigate(`/topicexplorer?tech=${specialization}`)}
         className="px-8 py-3 bg-[#843FEF] text-white rounded-xl font-bold hover:bg-[#742FEF] transition-all uppercase text-xs tracking-widest shadow-lg"
       >
         Go to Explorer
       </button>
     </div>
   </div>
 )}

 <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16 max-w-6xl mx-auto">
 {plans.filter(p => {
   // Add-on logic:
   // 3 month users (quarterly) -> 6m, 12m
   // 6 month users (halfyearly) -> 12m
   // 12 month users (yearly) -> none
   if (!subscription.isPaid || subscription.plan === 'free') return true;
   
   const currentPlan = subscription.plan?.toLowerCase();
   if (currentPlan === 'quarterly' || currentPlan?.includes('3 month')) {
     return p.id === 'halfyearly' || p.id === 'yearly';
   }
   if (currentPlan === 'halfyearly' || currentPlan?.includes('6 month')) {
     return p.id === 'yearly';
   }
   if (currentPlan === 'yearly' || currentPlan?.includes('1 year')) {
     return false;
   }
   return true;
 }).map((p) => {
   const planActualDiscountAmount = p.discountAmount || 0;
   return (
 <div
 key={p.id}
 className={`rounded-[32px] border-2 ${selectedPlanId === p.id ? 'border-primary shadow-[0_0_40px_rgba(79, 70, 229,0.3)]' : 'border-accentGray/30'}
 bg-bgSection p-8 flex flex-col shadow-2xl transition-all hover:scale-[1.03] hover:border-primary/50 relative group overflow-hidden`}
 >
 {/* Card background glow */}
 <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-full blur-2xl group-hover:bg-primary/10 transition-all"></div>

 {/* Background "Months" Text */}
 <div className="absolute -right-4 -bottom-4 text-7xl font-black text-white/[0.03] select-none pointer-events-none uppercase tracking-tighter group-hover:text-white/[0.05] transition-all duration-700">
   {p.duration.split(' ')[0]}
 </div>

 <div className="mb-6 relative z-10">
 {p.discount && (
 <div className="absolute -top-2 -right-2 bg-red-600 text-white text-[10px] font-extrabold px-3 py-1 rounded-full shadow-lg uppercase tracking-tighter">
 {p.savingsLabel}
 </div>
 )}
 <div className="text-xs text-primary font-bold mb-2 uppercase tracking-widest">{p.tagline}</div>
 <div className="text-white text-3xl font-extrabold mb-1 tracking-tighter">{p.label}</div>
 <div className="text-primary text-sm font-bold uppercase tracking-tighter mb-6">{p.duration}</div>
 
 {/* Pricing */}
 <div className="mt-4">
 {planActualDiscountAmount > 0 && (
                             <div className="flex flex-col gap-1 mb-3">
                               <div className="flex items-center gap-2">
                                 <span className="text-lg text-muted line-through font-bold">
                                   ₹{p.originalPrice.toLocaleString('en-IN')}
                                 </span>
                                 <span className="text-xs bg-red-500/10 text-red-400 px-2 py-0.5 rounded-lg font-bold">
                                   SAVE ₹{planActualDiscountAmount.toLocaleString('en-IN')}
                                 </span>
                               </div>
                               <div className="bg-primary/10 text-primary text-[10px] font-black px-3 py-1 rounded-full w-fit uppercase tracking-tighter">
                                 {p.scholarshipType} Scholarship Applied
                               </div>
                             </div>
                        )}
 <div className="flex flex-col gap-1">
 <div className="flex items-baseline gap-1">
 <span className="text-5xl font-black text-primary">
 ₹{p.payableAmount.toLocaleString('en-IN')}
 </span>
 {p.hasGst && <span className="text-sm font-bold text-gray-500 ml-1">+ 18% GST</span>}
 </div>
 </div>
 <div className="text-[10px] text-green-400 font-bold uppercase tracking-widest mt-2">
   {buyerCount < 100 ? 'Exclusive Early Bird Price' : 'Standard Premium Price'}
 </div>
 </div>
 </div>
 
 <ul className="text-sm text-body/80 space-y-3 flex-1 mt-4 relative z-10">
 {allFeatures.map((f, i) => (
 <li key={i} className="flex items-start gap-3">
 <span className="text-primary mt-1">✓</span>
 <span>{f}</span>
 </li>
 ))}
 </ul>

 <button
 onClick={() => startCheckout(p.id)}
 className="mt-8 w-full btn-primary-gradient py-4 rounded-2xl font-black text-lg shadow-xl hover:scale-105 active:scale-95 transition-all uppercase tracking-tighter"
 >
 Choose Plan
 </button>
 </div>
 )})}
 </div>
 
 <div className="rounded-[32px] border border-accentGray/30 bg-bgSection p-10 text-sm text-muted text-center shadow-2xl relative overflow-hidden">
 <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-primary/5 rounded-full blur-3xl"></div>
 
        <button
          onClick={startTrial}
          disabled={hasUsedTrial || isStartingTrial || isTeamUser}
          className={`mb-6 w-full py-4 rounded-2xl font-extrabold transition-all shadow-xl uppercase tracking-widest ${
            (hasUsedTrial || isTeamUser)
              ? 'bg-white/5 text-muted cursor-not-allowed border border-accentGray/50'
              : isStartingTrial
              ? 'bg-bgSection text-primary border border-primary/30 animate-pulse'
              : 'bg-bgMain text-primary border border-primary/30 hover:bg-primary hover:text-heading'
          }`}
        >
          {isStartingTrial 
            ? 'Activating Trial...'
            : isTeamUser
            ? 'Trial Unavailable for Team'
            : hasUsedTrial 
            ? 'Trial Already Used' 
            : '🚀 Start 7-day LLM Free Trial'}
        </button>
        {isTeamUser && (
          <p className="text-primary font-bold text-xs mb-4 uppercase tracking-tighter">
            Free trial is restricted for team roles and bootcamp participants.
          </p>
        )}
        {hasUsedTrial && !isTeamUser && (
          <p className="text-primary font-bold text-xs mb-4 uppercase tracking-tighter">
            You've already activated your one-time LLM trial period.
          </p>
        )}
        {!hasUsedTrial && !isTeamUser && (
          <p className="text-muted font-medium text-xs mb-6 max-w-md mx-auto">
            Free trial grants access to premium AI mentor features only. Subscribe for full platform access and TSG AI.
          </p>
        )}
 <p className="font-medium">
 By subscribing, you agree to our{' '}
 <a href="/terms-of-service" className="text-primary font-bold underline hover:text-primary transition">Terms of Service</a>
 {' '}and{' '}
 <a href="/privacy-policy" className="text-primary font-bold underline hover:text-primary transition">Privacy Policy</a>.
 </p>
 </div>

 {/* Payment History Section */}
 <div className="mt-12 rounded-[32px] border border-accentGray/30 bg-bgSection p-8 shadow-2xl">
 <button
 onClick={() => setShowPaymentHistory(!showPaymentHistory)}
 className="w-full flex items-center justify-between text-primary font-extrabold text-xl uppercase tracking-tighter hover:text-white transition-colors"
 >
 <span className="flex items-center gap-3">
 <span className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">📋</span>
 Payment History
 </span>
 <span className="text-2xl w-10 h-10 rounded-full bg-bgMain flex items-center justify-center transition-transform" style={{ transform: showPaymentHistory ? 'rotate(180deg)' : 'rotate(0deg)' }}>
 {showPaymentHistory ? '−' : '+'}
 </span>
 </button>
 
 {showPaymentHistory && (
 <div className="mt-10">
 {paymentHistoryLoading ? (
 <div className="text-center text-muted py-12">
 <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-4"></div>
 Fetching your history...
 </div>
 ) : payments.length === 0 ? (
 <div className="text-center text-muted py-12 border-2 border-dashed border-accentGray/30 rounded-3xl">
 No payment history found. Subscribe to begin your premium journey!
 </div>
 ) : (
 <div className="space-y-6">
 {/* Summary */}
 <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
 <div className="bg-white/5 rounded-2xl p-6 text-center border border-accentGray/30 shadow-inner">
 <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-2">Total Payments</p>
 <p className="text-white text-3xl font-black">{payments.length}</p>
 </div>
 <div className="bg-white/5 rounded-2xl p-6 text-center border border-accentGray/30 shadow-inner">
                     <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-2">Total Investment</p>
                     <p className="text-primary text-3xl font-black">
                       ₹{payments.reduce((sum, p) => sum + (Number(p.payable_amount || p.amount || 0)), 0).toLocaleString('en-IN')}
                     </p>
 </div>
 <div className="bg-white/5 rounded-2xl p-6 text-center border border-accentGray/30 shadow-inner">
 <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-2">Active Plans</p>
 <p className="text-green-400 text-3xl font-black">
 {payments.filter(p => {
 if (!p.end_date) return false;
 return new Date(p.end_date) > new Date();
 }).length}
 </p>
 </div>
 </div>

 {/* Payment List */}
 <div className="space-y-4 max-h-[500px] overflow-y-auto custom-scrollbar pr-2">
 {payments.filter(payment => {
   const amount = Number(payment.payable_amount || payment.amount || 0);
   // Only show 3, 6, 1 year payments based on common price points or plan names
   // quarterly: ~4679-4719, halfyearly: ~14999, yearly: ~18999
   const planName = (payment.name || payment.planName || payment.plan_name || "").toLowerCase();
   return planName.includes('3 month') || planName.includes('6 month') || planName.includes('1 year') ||
          planName.includes('quarterly') || planName.includes('halfyearly') || planName.includes('yearly') ||
          [3999, 4679, 4718, 4719, 7999, 14999, 18999].includes(amount);
 }).map((payment, index) => {
 const startDate = payment.start_date ? new Date(payment.start_date) : new Date(payment.created_at);
 const endDate = payment.end_date ? new Date(payment.end_date) : null;
 const now = new Date();
 const isActive = endDate && endDate > now;
 const isExpired = endDate && endDate <= now;
 const daysRemaining = endDate ? Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)) : null;
 
 let planDuration ="N/A";
 if (payment.amount === 2999) planDuration ="30 days";
 else if (payment.amount === 7999) planDuration ="90 days";
 else if (payment.amount === 14999) planDuration ="180 days";
 
 return (
 <div
 key={payment.id || index}
 className="bg-bgMain rounded-3xl p-6 border border-accentGray/30 hover:border-primary/30 transition-all shadow-xl group"
 >
 <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
 <div className="flex-1">
 <div className="flex flex-wrap items-center gap-3 mb-4">
 <h4 className="text-white font-extrabold text-lg break-words whitespace-normal overflow-visible">
 {(() => {
 if (payment.payment_method === 'llm_trial' ||
 payment.plan_name === 'llm_trial_7days' ||
 (payment.amount === 0 && payment.payment_method !== 'razorpay' && payment.payment_method !== 'trial')) {
 return '✨ 7 Days Free Trial';
 }
 const pName = payment.name || payment.planName || payment.plan_name ||"Premium Subscription";
 if (pName === 'skillgenie_premium_3months' || pName.toLowerCase().includes('skillgenie_premium_3')) return 'Premium (3 Months)';
 if (pName.toLowerCase().includes('premium') && pName.toLowerCase().includes('6')) return 'Premium (6 Months)';
 if (pName.toLowerCase().includes('elite') || pName.toLowerCase().includes('yearly')) return 'Elite (1 Year)';
 return pName;
 })()}
 </h4>
 <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
 payment.status === 'success' 
 ? (isActive ? 'bg-green-500 text-black shadow-lg shadow-green-500/20' : isExpired ? 'bg-red-500/20 text-red-400' : 'bg-primary text-white')
 : 'bg-red-500/20 text-red-400'
 }`}>
 {payment.status === 'success' 
 ? (isActive ? 'Active' : isExpired ? 'Expired' : 'Completed')
 : payment.status}
 </span>
 </div>
 <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-xs font-bold uppercase tracking-widest">
                         <div className="flex flex-col gap-1">
                           <span className="text-muted">Amount</span>
                           <span className="text-primary">₹{(payment.payable_amount || payment.amount || 0).toLocaleString('en-IN')}</span>
                         </div>
 <div className="flex flex-col gap-1">
 <span className="text-muted">Duration</span>
 <span className="text-body">{planDuration}</span>
 </div>
 {startDate && (
 <div className="flex flex-col gap-1">
 <span className="text-muted">Started</span>
 <span className="text-body">{startDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
 </div>
 )}
 {endDate && (
 <div className="flex flex-col gap-1">
 <span className="text-muted">Ends On</span>
 <span className="text-body">{endDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
 </div>
 )}
 </div>
 {isActive && daysRemaining !== null && (
 <div className="flex items-center gap-2 mt-4">
 <div className="flex-1 h-1 bg-white/5 rounded-full overflow-hidden">
 <div className="h-full bg-green-400 animate-pulse" style={{ width: `${Math.min((daysRemaining / 30) * 100, 100)}%` }}></div>
 </div>
 <span className="text-green-400 text-[10px] font-black">{daysRemaining} DAYS LEFT</span>
 </div>
 )}
 </div>
 <div className="flex flex-col gap-2 shrink-0">
 <button
 onClick={() => {
 let planName = payment.name || payment.planName || payment.plan_name ||"Premium Subscription";
 if (payment.payment_method === 'llm_trial' || 
 payment.plan_name === 'llm_trial_7days' ||
 (payment.amount === 0 && payment.payment_method !== 'razorpay' && payment.payment_method !== 'trial')) {
 planName = '7 Days Free Trial';
 }
 const receiptData = `
<!DOCTYPE html>
<html>
<head>
    <style>
        body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background-color: #f0f9ff; display: flex; justify-content: center; align-items: center; min-height: 100vh; margin: 0; }
        .receipt-container { background-color: #ffffff; width: 450px; padding: 40px; border-radius: 20px; box-shadow: 0 20px 50px rgba(31, 138, 203, 0.1); border: 1px solid #e0f2fe; text-align: center; }
        .header { margin-bottom: 30px; }
        .logo { font-size: 28px; font-weight: 900; color: #1f8acb; text-transform: uppercase; letter-spacing: -1px; margin-bottom: 5px; }
        .powered-by { font-size: 10px; font-weight: 800; color: #94a3b8; text-transform: uppercase; letter-spacing: 2px; }
        .divider { height: 1px; background: linear-gradient(to right, transparent, #e2e8f0, transparent); margin: 25px 0; }
        .receipt-title { font-size: 14px; font-weight: 900; color: #0f172a; text-transform: uppercase; letter-spacing: 3px; margin-bottom: 30px; }
        .details-grid { text-align: left; display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px; }
        .detail-item { display: flex; flex-direction: column; gap: 4px; }
        .label { font-size: 9px; font-weight: 800; color: #64748b; text-transform: uppercase; letter-spacing: 1px; }
        .value { font-size: 14px; font-weight: 700; color: #1e293b; }
        .amount-section { background-color: #f8fafc; padding: 20px; rounded: 12px; margin: 30px 0; border: 1px dashed #cbd5e1; }
        .amount-label { font-size: 11px; font-weight: 800; color: #64748b; text-transform: uppercase; margin-bottom: 5px; }
        .amount-value { font-size: 32px; font-weight: 900; color: #1f8acb; }
        .footer { font-size: 11px; color: #94a3b8; font-weight: 600; line-height: 1.6; }
        .copyright { margin-top: 20px; font-size: 9px; color: #cbd5e1; }
    </style>
</head>
<body>
    <div class="receipt-container">
        <div class="header">
            <div class="logo">
                <img src="/logoo.jpg" alt="The Skill Genie" style="height: 60px; margin-bottom: 10px;">
            </div>
            <div class="powered-by">Powered by EvolveX Innovations</div>
            <div style="font-size: 10px; color: #64748b; margin-top: 5px; font-weight: bold;">GSTIN: 37BABPP4434R1Z4</div>
        </div>
        
        <div class="divider"></div>
        <div class="receipt-title">Official Payment Receipt</div>
        
        <div class="details-grid">
            <div class="detail-item">
                <span class="label">Receipt No</span>
                <span class="value">SG-${payment.razorpay_payment_id?.slice(-8).toUpperCase() || Date.now().toString().slice(-8)}</span>
            </div>
            <div class="detail-item">
                <span class="label">Date</span>
                <span class="value">${new Date().toLocaleDateString('en-IN')}</span>
            </div>
            <div class="detail-item">
                <span class="label">Plan Type</span>
                <span class="value">${planName}</span>
            </div>
            <div class="detail-item">
                <span class="label">Duration</span>
                <span class="value">${planDuration}</span>
            </div>
            <div class="detail-item" style="grid-column: span 2;">
                <span class="label">Payment ID</span>
                <span class="value">${payment.razorpay_payment_id || 'N/A'}</span>
            </div>
        </div>

            <div class="amount-section">
                <div class="amount-label">Amount Paid</div>
                <div class="amount-value">₹${payment.amount?.toLocaleString('en-IN')}</div>
            </div>

        <div class="footer">
            <div>Thank you for choosing SkillGenie!</div>
            <div>Support: support@skillgenie.com | www.skillgenie.com</div>
        </div>
        
        <div class="copyright">© 2026 EvolveX Innovations - Pvt Ltd. All rights reserved.</div>
    </div>
</body>
</html>`;
const blob = new Blob([receiptData], { type: 'text/html' });
const url = URL.createObjectURL(blob);
const a = document.createElement('a');
a.href = url;
a.download = `Receipt_${payment.razorpay_payment_id || payment.id || Date.now()}.html`;
a.click();
URL.revokeObjectURL(url);
 }}
 className="px-6 py-2.5 bg-primary/10 text-primary border border-primary/30 rounded-xl hover:bg-primary hover:text-white transition-all text-xs font-black uppercase tracking-widest shadow-lg"
 >
 Receipt ⬇
 </button>
 </div>
 </div>
 </div>
 );
 })}
 </div>
 </div>
 )}
 </div>
 )}
 </div>
      {/* Already Subscribed Modal */}
      {showAlreadySubscribedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-bgSection rounded-3xl border border-primary/30 p-8 shadow-2xl max-w-md mx-4 animate-fadeIn">
            <h3 className="text-2xl font-extrabold text-white mb-4 flex items-center gap-2">
              <span className="text-3xl">✅</span>
              Premium Access Active
            </h3>
            <p className="text-gray-300 mb-6 leading-relaxed">
              You already have an active premium subscription! Your access has been unlocked.
            </p>
            <div className="bg-primary/10 rounded-xl p-4 mb-6 border border-primary/20">
              <p className="text-sm text-gray-200">
                You can continue learning with all premium features, or renew/upgrade when your current plan expires.
              </p>
            </div>
            <button
              onClick={() => setShowAlreadySubscribedModal(false)}
              className="w-full py-3 bg-primary text-white rounded-xl font-bold hover:bg-primary/90 transition-all flex items-center justify-center gap-2"
            >
              <IoArrowBack className="h-5 w-5" />
              Back to Plans
            </button>
          </div>
        </div>
      )}
 </main>
 </div>
 );
}




